import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { useParams, useNavigate, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { MainLayout } from "layouts";
import { getQnaDetail, answerDelete } from "api/qna";
import Text from "components/ui/Text";
import Button from "components/entrance/Button";
import Confirm from "components/ui/Confirm";
import { specialFormatDate } from "utils/date";
import { setQnaList } from "redux/slices/qna/qnaSlice";
import Error from "layouts/Error";

const ContentCard = ({ answer, name, date, qustionData, onEdit, onDelete }) => {
  return (
    <QuestionCard>
      <div>
        <Text color="gray80" fontSize="14px" fontWeight="700" data-clarity-mask="True">
          {name}
        </Text>
        <Text fontSize="14px" fontWeight="500">
          {specialFormatDate(date)}
        </Text>
      </div>

      <Text color="gray80" lineHeight="24px">
        {qustionData}
      </Text>
      {answer && (
        <div>
          <Text color="gray50" fontSize="14px" onClick={onEdit}>
            수정
          </Text>
          <Text
            color="gray50"
            fontSize="14px"
            css={css`
              margin: 0 10px;
            `}
          >
            ∙
          </Text>
          <Text color="gray50" fontSize="14px" onClick={onDelete}>
            삭제
          </Text>
        </div>
      )}
    </QuestionCard>
  );
};

const QnaDetail = () => {
  const { seqQna } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { title, userName, date, qustionData } = location.state;
  const [data, setData] = useState({
    title: "",
    userName: "",
    date: "",
    qustionData: "",
    answerData: [],
    isError: false,
  });

  const { seqPartner, seqPartnerBranch } = useSelector(
    (state) => state.partner,
  );

  useEffect(() => {
    async function fetchQna() {
      try {
        const data = await getQnaDetail({
          uploadId: seqQna,
          seqPartner,
          seqPartnerBranch,
          criteria: {
            page: 1,
            pageSize: 10,
          },
        });
        setData((previousValue) => ({
          ...previousValue,
          answerData: data.results,
        }));
      } catch (error) {
        setData((previousValue) => ({
          ...previousValue,
          isError: true,
        }));
      }
    }
    fetchQna();
  }, []);

  const handleClickEdit = (commentSeq, coachName, content) => {
    navigate(`/all/qna/answer/${seqQna}`, {
      state: {
        commentSeq,
        coachName,
        content,
      },
    });
  };
  const handleClickDelete = (commentSeq) => {
    Confirm("", {
      title: "답변내용을 삭제할까요?",
      button: {
        left: { name: "취소", color: "gray20" },
        right: { name: "삭제하기", color: "primary50" },
      },
      callback: (result) => {
        if (result) {
          answerDelete({
            uploadId: seqQna,
            commentId: commentSeq,
            seqPartner,
            seqPartnerBranch,
          })
            .then((result) => {
              if (result) {
                const answerDataLen = data.answerData.length;
                setData((previousValue) => ({
                  ...previousValue,
                  answerData: previousValue.answerData.filter(
                    (v) => v.commentId !== commentSeq,
                  ),
                }));
                if (answerDataLen === 1) {
                  dispatch(setQnaList({ seqQna, answerCompleteYn: "N" }));
                }
              }
            })
            .catch((e) => {});
        }
      },
    });
  };

  return (
    <MainLayout
      withBottomTab={false}
      withHeader={true}
      backgroundColor="white"
      headerProps={{
        padding: "0 20px 0 0",
        withBackButton: true,
        centerTitle: "",
      }}
    >
      <Wrapper>
        <WrapperTop>
          <Text
            display="block"
            fontSize="22px"
            fontWeight="600"
            lineHeight="32px"
          >
            {title}
          </Text>
          <Text
            fontWeight="600"
            css={css`
              margin-top: 24px;
            `}
          >
            문의내용
          </Text>
          <ContentCard
            answer={false}
            name={userName}
            date={date}
            qustionData={qustionData}
          />
          {data.isError ? (
            <AnswerCard>
              <Error mode="section" />
            </AnswerCard>
          ) : (
            <AnswerCard>
              {data?.answerData?.length === 0 ? (
                <EmptyAnswer>
                  <Text color="gray20" fontSize="18px" lineHeight="28x">
                    답변내용이 아직 없습니다.
                  </Text>
                </EmptyAnswer>
              ) : (
                <>
                  <Text
                    fontWeight="600"
                    css={css`
                      margin-top: 24px;
                    `}
                  >
                    답변내용
                  </Text>
                  {data?.answerData?.map((v) => (
                    <ContentCard
                      key={v.commentId}
                      answer={true}
                      name={v.regCoachName}
                      date={v.updateDt || v.regDt}
                      qustionData={v.commentContent}
                      onEdit={() =>
                        handleClickEdit(
                          v.commentId,
                          v.regCoachName,
                          v.commentContent,
                        )
                      }
                      onDelete={() => handleClickDelete(v.commentId)}
                    />
                  ))}
                </>
              )}
            </AnswerCard>
          )}
        </WrapperTop>
        <WrapperBtm>
          <Button
            color="primary100"
            background="#eef5ff"
            onClick={() => navigate(-1)}
          >
            확인
          </Button>
          <Button
            color="white"
            background="primary100"
            onClick={() =>
              navigate(`/all/qna/answer/${seqQna}`, {
                state: {
                  dataLen: data.answerData.length,
                },
              })
            }
          >
            답변
          </Button>
        </WrapperBtm>
      </Wrapper>
    </MainLayout>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: calc(100% - 50px);
  padding: 16px 20px 42px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const WrapperTop = styled.div`
  overflow-y: scroll;
`;

const QuestionCard = styled.div`
  margin-top: 8px;
  padding: 16px;
  background: #f3f6fd;
  border-radius: 6px;
  div:first-of-type {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  div:last-of-type {
    text-align: right;
  }
`;

const AnswerCard = styled.div``;
const EmptyAnswer = styled.div`
  margin-top: 48px;
  text-align: center;
`;

const WrapperBtm = styled.div`
  display: flex;
  margin-top: 20px;
  button + button {
    margin-left: 10px;
    margin-top: 0px !important;
  }
`;

export default QnaDetail;
